<template>
  <v-card flat>
    <!-- ask user to override capacities -->
    <v-card-text v-show="askToOverrideCapacitiesMode" class="max-width-400">
      <v-row>
        <v-col class="col-12 headline-standard">
          {{ $t('capacityOverride.reallyChange') }}
        </v-col>
        <!-- iterate products -->
        <v-col
          v-for="(
            capacities, productId, productAutoIndex
          ) in capacitiesToOverride"
          :key="productAutoIndex"
          class="col-12 pt-1"
        >
          <v-row>
            <!-- product title -->
            <v-col class="col-12 pt-1 pb-0 font-weight-bold">
              <span
                v-if="capacities && capacities.length && capacities[0].event"
              >
                {{ capacities[0].event.title }}
              </span>
              <span v-else>
                {{ capacities[0].product.getTitle() }}
              </span>
              <v-divider />
            </v-col>
          </v-row>
          <!-- iterate capacities of the product -->
          <v-row
            v-for="(capacity, capacityIndex) in capacities"
            :key="capacityIndex"
          >
            <!-- capacity value -->
            <v-col v-if="capacity.capacity.getValue()" class="col-3 pb-0 pt-1">
              {{ capacity.capacity.getTitle() }}
            </v-col>
            <!-- old capacity -->
            <v-col class="col-auto pb-0 pt-1">
              {{ capacity.capacity.getValidCapacity() }}
            </v-col>
            <!-- arrow to new capacity -->
            <v-col class="col-auto pb-0 pt-1">
              <span class="fal fa-arrow-right" />
            </v-col>
            <!-- new capacity -->
            <v-col class="col-auto pb-0 pt-1">
              {{ capacity.capacity.getCapacity() }}
            </v-col>
            <v-col class="col-5 pb-0 pt-1 pr-0">
              <v-row class="justify-end">
                <capacity-bellow-warning :capacity="capacity.capacity" />
              </v-row>
            </v-col>
          </v-row>
        </v-col>

        <v-col class="col-12 pb-0 font-weight-bold">
          {{ $t('capacityOverride.willBeOverwrittenOn') }}
          <v-divider />
        </v-col>
        <!-- iterate dates -->
        <v-col class="col-12 pt-1">
          <span v-for="(date, dateIndex) in datesToOverride" :key="dateIndex">
            {{ moment(date).format('DD.MM.YYYY')
            }}<span v-if="dateIndex < datesToOverride.length - 1">,</span>
          </span>
        </v-col>
      </v-row>
      <v-row class="justify-space-between">
        <!-- cancel button -->
        <v-col class="col-auto text-center">
          <v-btn
            outlined
            color="primary"
            @click="askToOverrideCapacitiesMode = false"
          >
            {{ $t('general.cancel') }}
          </v-btn>
        </v-col>
        <!-- override button -->
        <v-col class="col-auto text-center">
          <v-btn outlined color="red" @click="overrideCapacities()">
            {{ $t('valueCapacity.capacityUpdated') }}
          </v-btn>
        </v-col>
      </v-row>
    </v-card-text>
    <!-- form logic -->
    <v-card-text v-show="!askToOverrideCapacitiesMode">
      <!-- iterate products -->
      <v-form ref="overrideForm">
        <v-row>
          <v-col
            v-for="(
              entriesByProduct, entriesByProductIndex, productIndex
            ) in selectedEntriesGroupedByProduct"
            :key="entriesByProductIndex"
            class="py-0 px-0 max-width-300 min-width-300"
            :class="'col-' + 12 / (selectedEntriesGroupedByProduct.length + 1)"
          >
            <v-row class="no-gutters">
              <!-- product title -->
              <v-col class="col-10 content-4 align-self-center">
                <!-- event -->
                <span v-if="entriesByProduct[0].event">
                  {{ entriesByProduct[0].event.title }}
                </span>
                <span v-else>{{ entriesByProduct[0].product.getTitle() }}</span>
              </v-col>
              <!-- edit event / product -->
              <!-- PERMISSION RELEVANT !! -->
              <v-col
                v-if="entriesByProduct[0].event && canIEditEvents"
                class="col-2 text-right"
                @click="editEvent(entriesByProduct[0].event)"
              >
                <v-btn icon x-small color="primary">
                  <span class="fal fa-pen" />
                </v-btn>
              </v-col>

              <!-- alert -->
              <v-col
                v-if="selectedCapacityAlert"
                class="col-12"
                @click="selectedCapacityAlert = false"
              >
                <v-alert outlined dismissible dense color="red">
                  {{ $t('capacityOverride.noCapacitiesSelected') }}
                  <span class="fal fa-reply fa-rotate-270 ml-2" />
                </v-alert>
              </v-col>

              <!-- do not show capacity settings, if there are none -->
              <div
                v-if="
                  entriesByProduct[0].product
                    .getProductCapacity()
                    .hasCapacityOfAnyType()
                "
                class="w-100"
              >
                <!-- iterate capacities per product -->
                <v-col class="col-12 pa-0">
                  <v-row
                    :id="'scroll' + entriesByProductIndex"
                    class="max-height-200 scrollable-y"
                  >
                    <v-col class="col-12 pa-0">
                      <v-list :disabled="disabled">
                        <v-list-item-group
                          :value="entriesByProduct"
                          multiple
                          color="primary"
                        >
                          <v-list-item
                            v-for="(
                              capacityEntry, capacityEntryIndex
                            ) in entriesByProduct"
                            :key="capacityEntryIndex"
                            dense
                            class="py-0 px-1"
                            color="primary"
                            :input-value="capacityEntry"
                            @click="
                              addCapacityToOverride(
                                entriesByProductIndex,
                                capacityEntry
                              )
                            "
                          >
                            <v-list-item-content class="py-0">
                              <v-list-item-title>
                                <v-row>
                                  <!-- title -->
                                  <v-col class="col-8 pa-0 align-self-center">
                                    <!-- capacities -->
                                    <p
                                      v-if="
                                        capacityEntry.capacity &&
                                        capacityEntry.capacity.value
                                      "
                                      class="mb-0 ellipsis"
                                    >
                                      {{ capacityEntry.capacity.getTitle() }}
                                    </p>
                                    <!-- product title -->
                                    <p v-else class="mb-0 ellipsis">
                                      <template v-if="capacityEntry.event">{{
                                        capacityEntry.event.title
                                      }}</template>
                                      <template v-else>{{
                                        capacityEntry.product.getTitle()
                                      }}</template>
                                    </p>
                                  </v-col>
                                  <!-- capacity -->
                                  <v-col
                                    v-if="capacityEntry.capacity"
                                    class="col-4 text-center py-1 white--text rounded"
                                    :class="
                                      capacityEntry.capacity.getStockLeftColor()
                                    "
                                  >
                                    {{ capacityEntry.capacity.getCount() }} /
                                    {{ capacityEntry.capacity.getCapacity() }}
                                  </v-col>
                                  <!-- warning if capacity fall bellow -->
                                  <capacity-bellow-warning
                                    :capacity="capacityEntry.capacity"
                                    class="my-1 ml-3"
                                  />
                                </v-row>
                              </v-list-item-title>
                            </v-list-item-content>
                          </v-list-item>
                        </v-list-item-group>
                      </v-list>
                    </v-col>
                  </v-row>
                </v-col>
                <!-- new capacity input field -->
                <v-col v-if="showOverride" class="col-12 pb-0">
                  <v-text-field
                    outlined
                    type="number"
                    :label="$t('capacityCalendar.newCapacity')"
                    :value="newCapacityAmounts[productIndex]"
                    :rules="[
                      (v) =>
                        capacityAmountRule(v, entriesByProduct) ||
                        $t('capacityOverride.setCapacityAmount'),
                    ]"
                    @input="
                      (newAmount) => {
                        changeCapacityAmounts(entriesByProductIndex, newAmount)
                      }
                    "
                  />
                </v-col>
                <!-- has sibling products ? -->
                <v-col
                  v-if="
                    entriesByProduct[0].product.getCapacityTransferTo() &&
                    !products.find(
                      (product) =>
                        product.getId() ===
                        entriesByProduct[0].product.getCapacityTransferTo()
                    ) &&
                    !hideSiblingProduct
                  "
                  class="col-12 pb-0 text-center clickable hover-underline"
                  @click="
                    $emit(
                      'loadAdditionalProduct',
                      entriesByProduct[0].product.getCapacityTransferTo()
                    )
                  "
                >
                  <span class="fal fa-chevron-left mr-1" />
                  {{ $t('capacityCalendar.showCapacitySibling') }}
                </v-col>
                <!-- hide sibling product -->
                <v-col
                  v-if="
                    products.find(
                      (product) =>
                        product.getCapacityTransferTo() ===
                        entriesByProduct[0].product.getId()
                    )
                  "
                  class="col-12 text-center clickable hover-underline"
                  @click="
                    $emit(
                      'removeAdditionalProduct',
                      entriesByProduct[0].product.getId()
                    )
                  "
                >
                  {{ $t('capacityCalendar.disableCapacitySibling') }}
                  <span class="fal fa-chevron-right ml-1" />
                </v-col>

                <!-- override capacity -->
                <v-col
                  v-if="
                    !showOverride &&
                    canIEditCapacities &&
                    productIndex ===
                      Object.keys(selectedEntriesGroupedByProduct).length - 1 &&
                    !disabled
                  "
                  class="col-12 pt-0"
                >
                  <v-row @click="showOverride = !showOverride">
                    <v-col
                      class="col-12 pb-0 align-self-center text-center clickable hover-underline"
                    >
                      {{ $t('capacityCalendar.selectOverrideCapacity') }}
                      <span class="fal fa-chevron-right ml-1" />
                    </v-col>
                  </v-row>
                </v-col>
              </div>
            </v-row>
          </v-col>
          <!-- override capacity -->
          <v-col
            v-if="showOverride"
            class="pt-0 max-width-400 min-width-300"
            :class="'col-' + 12 / (selectedEntriesGroupedByProduct.length + 1)"
          >
            <!-- title and close button -->
            <v-row>
              <v-col class="col-9 content-2">
                {{ $t('valueCapacity.updateCapacity') }}
              </v-col>
              <v-col
                class="col-3 text-right pt-2 clickable"
                @click="showOverride = false"
              >
                <span class="fal fa-times fa-2x" />
              </v-col>
            </v-row>
            <!-- date selection -->
            <v-row>
              <!-- from date -->
              <v-col class="col-12 col-md-6 pb-0">
                <s-date-picker
                  :disabled="singleDate"
                  :date.sync="dateRangeToOverride.startDate"
                  :label="singleDate ? $t('general.date') : $t('general.from')"
                  :rules="[(v) => !!v || $t('v5.selectDate')]"
                />
              </v-col>

              <!-- to date -->
              <v-col v-if="!singleDate" class="col-12 col-md-6 pb-0">
                <s-date-picker
                  :date.sync="dateRangeToOverride.endDate"
                  :disabled="singleDate"
                  :label="$t('general.to')"
                  :rules="[(v) => !!v || $t('v5.selectDate')]"
                />
              </v-col>
            </v-row>

            <!-- show or disable rule based override -->
            <v-row>
              <v-col class="col-7 pt-0">
                <v-switch
                  v-model="showRuleBased"
                  :label="
                    showRuleBased
                      ? $t('capacityCalendar.hideRuleBased')
                      : $t('capacityCalendar.showRuleBased')
                  "
                  class="mt-0"
                  hide-details
                />
              </v-col>
              <v-col class="col-5 text-center pt-0">
                <v-btn
                  outlined
                  color="green"
                  class="white--text"
                  @click="askToOverrideCapacities()"
                >
                  {{ $t('capacityCalendar.override') }}
                </v-btn>
              </v-col>
            </v-row>

            <v-row
              v-if="selectedDatesAlert"
              @click="selectedDatesAlert = false"
            >
              <v-col class="col-12">
                <v-alert outlined dense dismissible color="red">
                  {{ $t('capacityOverride.noDatesSelected') }}
                  <span class="fal fa-reply fa-rotate-270 ml-2" />
                </v-alert>
              </v-col>
            </v-row>

            <!-- rule based overriding -->
            <v-row v-if="showRuleBased">
              <v-col class="col-12 pt-0">
                <!-- rule based date selector -->
                <rule-based-date-selector
                  :date-range="dateRangeToOverride"
                  hide-time-period
                  @changed="
                    (newDates) => {
                      datesToOverride = newDates
                    }
                  "
                />
                <v-card outlined>
                  <v-card-text>
                    <v-date-picker
                      v-model="datesToOverride"
                      full-width
                      :min="dateRangeToOverride.startDate"
                      :max="dateRangeToOverride.endDate"
                      first-day-of-week="1"
                      no-title
                      :show-current="false"
                      :locale="$store.getters.getLanguageLocale().slice(0, 2)"
                      multiple
                      color="primary"
                      class="elevation-0"
                    />
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-form>
    </v-card-text>
  </v-card>
</template>

<script>
import _ from 'lodash'
import { CapacityCalendarEntries } from '../../classes-internal/capacities/CapacityCalendarEntries'
import RuleBasedDateSelector from '../../components-shared/products/RuleBasedDateSelector'
import SDatePicker from '../../components-shared/utils/SDatePicker'
import BookingProgressBar from '../products/CapacityProgressBar'
import CapacityProgressBar from '../products/CapacityProgressBar'
import definitions from '../../../definitions'
import Products from '../../classes-shared/products/Products'
import moment from 'moment'
import CapacityBellowWarning from './CapacityBellowWarning'
import DateRange from '@/classes-shared/dates/DateRange'

export default {
  name: 'CapacityOverride',
  components: {
    CapacityBellowWarning,
    RuleBasedDateSelector,
    SDatePicker,
  },
  props: {
    selectedDate: {
      type: String, // like 2020-01-01
      required: true,
    },
    calendarEntries: {
      type: CapacityCalendarEntries,
      required: true,
    },
    products: {
      type: Array,
      required: true,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    // hide the button to load sibling products
    hideSiblingProduct: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      // v-switch
      showRuleBased: false,
      // v-switch
      showOverride: false,
      // date range to override the capacities
      dateRangeToOverride: new DateRange(null, null),
      datesToOverride: [],
      singleDate: false,
      capacitiesToOverride: {},
      newCapacityAmounts: {},
      // alerts
      selectedDatesAlert: false,
      selectedCapacityAlert: false,
      askToOverrideCapacitiesMode: false,
      moment,
      // simultaneously scrolling capacity sibling products
      scrollingPairs: [],
    }
  },

  computed: {
    selectedEntriesGroupedByProduct() {
      if (this.selectedDate) {
        let entriesByDate = this.calendarEntries.getCalendarEntryByDate(
          this.selectedDate
        )
        let groupedByProductId = _.groupBy(entriesByDate, (entry) => {
          if (entry.event) return 'event-' + entry.event.id
          return entry.product.getId()
        })
        return groupedByProductId
      }
      return []
    },
    appUser() {
      return this.$store.getters.getAppUserInstance()
    },
  },

  asyncComputed: {
    canIEditEvents: {
      async get() {
        if (this.appUser)
          return await this.appUser.doIHavePermissionInTheCurrentDestinationForKey(
            definitions.permissions.shopApi.BOOKING_CRUD_EVENTS
          )
        return false
      },
      default: false,
    },
    canIEditCapacities: {
      async get() {
        if (this.appUser)
          return await this.appUser.doIHavePermissionInTheCurrentDestinationForKey(
            definitions.permissions.shopApi.BOOKING_CRUD_PRODUCT_CAPACITY
          )
        return false
      },
      default: false,
    },
  },

  watch: {
    // set new date range
    selectedDate() {
      this.dateRangeToOverride.startDate = this.selectedDate
      this.dateRangeToOverride.endDate = this.selectedDate
    },
    showOverride() {
      this.$emit('showOverrideForm', this.showOverride)
    },
    // simultaneously scroll capacity sibling products
    products() {
      this.initParallelScrolling()
    },
  },

  methods: {
    // init simultaneously scrolling
    initParallelScrolling() {
      // reset scrolling pairs
      this.scrollingPairs = []
      // build scrolling pairs
      for (let i = 0; i < this.products.length; i++) {
        let product = this.products[i]
        if (product.getCapacityTransferTo()) {
          this.scrollingPairs.push({
            idA: product.getId(),
            idB: product.getCapacityTransferTo(),
          })
        }
      }

      // iterate scrolling pairs to set event listeners
      this.$nextTick(() => {
        for (let b = 0; b < this.scrollingPairs.length; b++) {
          let scrollingPair = this.scrollingPairs[b]
          // get html elements
          scrollingPair['elementA'] = document.getElementById(
            'scroll' + scrollingPair.idA
          )
          scrollingPair['elementB'] = document.getElementById(
            'scroll' + scrollingPair.idB
          )
          // build lockers
          scrollingPair['lockA'] = false
          scrollingPair['lockB'] = false
          if (scrollingPair.elementA && scrollingPair.elementB) {
            scrollingPair.elementA.addEventListener(
              'scroll',
              () => this.syncScroll('a', scrollingPair),
              false
            )
            scrollingPair.elementB.addEventListener(
              'scroll',
              () => this.syncScroll('b', scrollingPair),
              false
            )
          }
        }
      })
    },
    // simultaneously scrolling
    syncScroll(AorB, pair) {
      if (AorB === 'a') {
        if (pair.lockA) {
          pair.lockA = false
        } else {
          pair.lockB = true
          pair.elementB.scrollTop = pair.elementA.scrollTop
        }
      } else {
        if (pair.lockB) {
          pair.lockB = false
        } else {
          pair.lockA = true
          pair.elementA.scrollTop = pair.elementB.scrollTop
        }
      }
    },
    reset() {
      let products = new Products()
      products.products = this.products
      products.resetCapacityPreviews()
      this.capacitiesToOverride = {}
      this.newCapacityAmounts = {}
      this.buildCapacityPreview()
      this.showOverride = false
      this.showOverride = false
    },
    askToOverrideCapacities() {
      if (!this.readyToOverride()) return false
      this.askToOverrideCapacitiesMode = true
    },
    async overrideCapacities() {
      // instantiate Products class out of the products array
      let products = new Products()
      products.products = this.products
      // call method
      // IMPORTANT: new capacities are passed magically in the CapacityCalendarEntries as Value by reference
      await products.overrideCapacitiesRulebased(
        this.datesToOverride,
        this.selectedDate
      )
      // emit event to reload the capacities
      this.$emit('capacityOverridden')
      this.askToOverrideCapacitiesMode = false
    },

    // can we override the capacities ?
    readyToOverride() {
      // reset alarms
      this.selectedDatesAlert = false
      this.selectedCapacityAlert = false

      // check time periode and new capacity amounts
      if (!this.$refs.overrideForm.validate()) return false
      // generate selected dates if not in rule based mode
      if (!this.showRuleBased) {
        this.datesToOverride = []
        let startDate = new moment(this.dateRangeToOverride.startDate)
        let endDate = new moment(this.dateRangeToOverride.endDate)
        while (startDate.valueOf() <= endDate.valueOf()) {
          this.datesToOverride.push(startDate.format('YYYY-MM-DD'))
          startDate.add(1, 'd')
        }
      }
      // check dates to override
      if (!this.datesToOverride.length) {
        this.selectedDatesAlert = true
        return false
      }
      // check minimum one selected capacity value
      if (!this.capacitySelectionRule()) {
        this.selectedCapacityAlert = true
        return false
      }
      return true
    },

    // if a capacity amount was selected
    capacitySelectionRule() {
      // at leas one capacity selection was made
      if (!this.minimumOneCapacitySelected()) return false

      let productIds = Object.keys(this.newCapacityAmounts)
      // do we have for each new capacity amount (=== product) at least one selected capacity?
      for (let i = 0; i < productIds.length; i++) {
        let productId = productIds[i]
        if (!this.capacitiesToOverride[productId]?.length) return false
      }
      return true // all new capacity amounts have at least one selected capacity
    },

    minimumOneCapacitySelected() {
      for (let productId in this.capacitiesToOverride) {
        let capacityCalendarEntry = this.capacitiesToOverride[productId]
        if (capacityCalendarEntry?.length) return true
      }
      return false
    },

    // if a capacity was selected to override, you have to input a new capacity amount
    capacityAmountRule(inputValue, entriesByProduct) {
      let productId = entriesByProduct[0].product.getId()
      // handle event
      if (entriesByProduct[0]?.event)
        productId = 'event-' + entriesByProduct[0]?.event.id
      if (this.capacitiesToOverride[productId]?.length) {
        // we have to check
        if (inputValue && inputValue >= 0) return true
        return false // no input value given
      }
      return true // no check required
    },

    // helper method
    editEvent(event) {
      this.$router.push({
        name: 'editEvent',
        params: { event: event, id: event.id },
      })
    },
    addCapacityToOverride(productIndex, capacityEntry) {
      // create data structure first
      if (!this.capacitiesToOverride[productIndex])
        this.capacitiesToOverride[productIndex] = []
      // already selected?
      let foundIndex = this.capacitiesToOverride[productIndex].findIndex(
        (tmpCapacityEntry) => tmpCapacityEntry === capacityEntry
      )
      if (foundIndex > -1) {
        // remove already selected entry
        // first reset the preview
        this.capacitiesToOverride[productIndex][
          foundIndex
        ].capacity.resetCapacityPreview()
        this.capacitiesToOverride[productIndex].splice(foundIndex, 1)
      } else {
        // add new entry
        this.capacitiesToOverride[productIndex].push(capacityEntry)
      }
      this.buildCapacityPreview()
    },
    changeCapacityAmounts(productIndex, newAmount) {
      // create data structure, if not existing yet
      if (!this.newCapacityAmounts[productIndex])
        this.newCapacityAmounts[productIndex] = []
      // null will reset the preview capacity in ValueCapacity.js
      this.newCapacityAmounts[productIndex] = newAmount ? newAmount : null
      // rewrite selected capacity objects
      this.buildCapacityPreview()
    },
    /**
     * updates the ValueCapacity Instances for a preview in the UI
     */
    buildCapacityPreview() {
      // iterate grouped by product calendar entries
      for (let groupIndex in this.selectedEntriesGroupedByProduct) {
        // get amount for this product group
        let foundNewCapacityAmount = this.newCapacityAmounts[groupIndex]
        // null will reset the preview capacity in ValueCapacity.js
        if (foundNewCapacityAmount || foundNewCapacityAmount === null) {
          // find selected capacities for this new amount
          // iterate selected capacities
          let selectedCapacities = this.capacitiesToOverride[groupIndex]
          if (selectedCapacities) {
            // iterate selected capacities
            for (let i = 0; i < selectedCapacities.length; i++) {
              let selectedCapacity = selectedCapacities[i]
              selectedCapacity.capacity.setCapacityPreview(
                foundNewCapacityAmount
              )
            }
          }
        }
      }
      this.checkSingleDate()
    },
    // checks if there is an events selected to override. So set the component in singleDate mode
    checkSingleDate() {
      for (let productId in this.capacitiesToOverride) {
        let capacityCalendarEntries = this.capacitiesToOverride[productId]
        // iterate capacityCalendar
        for (let capacityCalendarEntryId in capacityCalendarEntries) {
          let capacityCalendarEntry =
            capacityCalendarEntries[capacityCalendarEntryId]
          if (capacityCalendarEntry.event) {
            this.dateRangeToOverride.startDate =
              capacityCalendarEntry.event.date
            this.dateRangeToOverride.endDate = capacityCalendarEntry.event.date
            this.singleDate = true
            return
          }
        }
      }
      this.singleDate = false
    },
  },
}
</script>
<style>
.ellipsis {
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
